import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { publicApi, fileUrl } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { money, merchCategoryLabel, tierRank, TIER_LABELS } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { ShoppingBag, Minus, Plus, Lock, Crown, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function MerchDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const { customer } = useCustomerAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/public/merch/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const soldOut = product?.in_stock === false;
  const locked = product?.vip_only && (!customer || tierRank(customer.vip_tier) < tierRank(product.vip_min_tier));

  const add = () => {
    if (locked) { navigate("/vip"); return; }
    addItem(product, qty);
    setOpen(true);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-5 py-12">
        <button onClick={() => navigate("/shop")} className="flex items-center gap-1 text-sm text-[var(--text-2)] hover:text-[var(--text)] mb-8" data-testid="back-to-shop">
          <ChevronLeft size={16} /> Back to Shop
        </button>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-10">
            <Skeleton className="aspect-square w-full bg-[var(--panel-2)] rounded-lg" />
            <div className="space-y-4"><Skeleton className="h-4 w-1/4 bg-[var(--panel-2)]" /><Skeleton className="h-8 w-3/4 bg-[var(--panel-2)]" /><Skeleton className="h-6 w-1/3 bg-[var(--panel-2)]" /><Skeleton className="h-24 w-full bg-[var(--panel-2)]" /></div>
          </div>
        ) : notFound || !product ? (
          <div className="text-center text-[var(--muted)] py-24" data-testid="merch-detail-notfound">
            <p className="mb-4">This product is no longer available.</p>
            <Link to="/shop" className="text-[#c9a86a] hover:text-[#b89455] transition-colors">Return to the shop</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-10" data-testid="merch-detail">
            <div>
              <div className="aspect-square bg-[var(--panel-2)] rounded-lg overflow-hidden border border-[var(--border-lch)]">
                {product.images?.[activeImg]?.url ? (
                  <img src={fileUrl(product.images[activeImg].url)} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><ShoppingBag size={48} /></div>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="flex gap-3 mt-3">
                  {product.images.map((im, i) => (
                    <button key={im.id || i} onClick={() => setActiveImg(i)} className={`h-16 w-16 rounded overflow-hidden border ${i === activeImg ? "border-[#c9a86a]" : "border-[var(--border-lch)]"}`}>
                      <img src={fileUrl(im.url)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[var(--muted)] tracking-wide uppercase">{merchCategoryLabel(product.category)}</span>
                {product.vip_only && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--panel-2)] text-[#c9a86a] border border-[#c9a86a]/40 flex items-center gap-1"><Crown size={10} /> VIP</span>}
                {product.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#c9a86a] text-[var(--on-gold)] font-semibold">FEATURED</span>}
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mt-2" data-testid="merch-detail-name">{product.name}</h1>
              <div className="mt-3 flex items-center gap-3">
                <span className="text-xl text-[#c9a86a] font-medium">{money(product.price)}</span>
                {product.compare_at_price && product.compare_at_price > product.price && (
                  <span className="text-[var(--muted)] line-through">{money(product.compare_at_price)}</span>
                )}
              </div>
              {product.short_description && <p className="text-[var(--text-2)] mt-4">{product.short_description}</p>}
              {product.description && <p className="text-[var(--text-2)] text-sm mt-3 leading-relaxed">{product.description}</p>}

              <div className="mt-6 text-xs">
                {soldOut ? <span className="text-red-400">Currently sold out</span>
                  : product.low_stock ? <span className="text-amber-300">Low stock — only a few left</span>
                  : <span className="text-emerald-300">In stock</span>}
              </div>

              {locked ? (
                <div className="mt-6 border border-[#c9a86a]/30 rounded-lg p-4 bg-[var(--panel)]" data-testid="merch-vip-gate">
                  <div className="flex items-center gap-2 text-[#c9a86a] text-sm font-medium"><Lock size={14} /> VIP Exclusive</div>
                  <p className="text-[var(--text-2)] text-sm mt-1">This piece is reserved for {TIER_LABELS[product.vip_min_tier] || "VIP members"} and above.</p>
                  <Button onClick={() => navigate("/vip")} className="mt-3 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none" data-testid="merch-join-vip">Join the VIP List</Button>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex items-center border border-[var(--border-2)] rounded">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-10 w-10 flex items-center justify-center text-[var(--text-2)] hover:text-[var(--text)]" data-testid="qty-dec"><Minus size={14} /></button>
                    <span className="w-10 text-center text-[var(--text)]" data-testid="qty-value">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} className="h-10 w-10 flex items-center justify-center text-[var(--text-2)] hover:text-[var(--text)]" data-testid="qty-inc"><Plus size={14} /></button>
                  </div>
                  <Button onClick={add} disabled={soldOut} data-testid="merch-add-to-cart" className="flex-1 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none disabled:opacity-40">
                    <ShoppingBag size={16} className="mr-2" /> {soldOut ? "Sold Out" : "Add to Cart"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
